import { getLocale } from '@/pages/cogo-marketing/i18n/locale-store'
import type {
  LibraryRagOrchestrationRequest,
  LibraryRagOrchestrationResult,
  LibraryRagOrchestrationStage,
} from '@cogo/agentic-generator/library-rag-agent-orchestration'
import {
  startLibraryRagApplicationGenerationFromPrompt,
  type StartAppGenerationOptions,
  type StartAppGenerationOutcome,
} from './start-app-generation-from-prompt'
import { validateStudioLibraryRagSuccessReceipt } from './studio-library-rag-receipt'
import {
  admitTrustedLegacyStudioLibraryRagResolution,
  classifyStudioLibraryRagRuntimeAuthority,
} from './studio-library-rag-runtime-authority'

export type StudioLibraryAssistantGenerationOptions = Omit<
  StartAppGenerationOptions,
  | 'designSystemPreset'
  | 'outcomeIntent'
  | 'sourceDesignSystemGeneration'
  // F5 — pack release authority is creator-path only; the library lane keeps
  // Design Ontology as its sole presentation authority.
  | 'designSystemPackRelease'
>

export interface StudioLibraryAssistant {
  generate(
    prompt: string,
    options?: StudioLibraryAssistantGenerationOptions,
  ): Promise<StartAppGenerationOutcome>
}

export class StudioLibraryAssistantError extends Error {
  override readonly name = 'StudioLibraryAssistantError'
  readonly code = 'studio_library_rag_rejected'
  readonly stage: LibraryRagOrchestrationStage
  readonly gapCodes: string[]

  constructor(input: {
    stage: LibraryRagOrchestrationStage
    gapCodes: string[]
  }) {
    const gapCodes = [...new Set(input.gapCodes)]
    super(['studio_library_rag_rejected', input.stage, ...gapCodes].join(':'))
    this.stage = input.stage
    this.gapCodes = gapCodes
  }
}

function rejectedLibraryRagError(
  result: Extract<LibraryRagOrchestrationResult, { ok: false }>,
): StudioLibraryAssistantError {
  return new StudioLibraryAssistantError({
    stage: result.stage,
    gapCodes: result.gaps.map((gap) => gap.code),
  })
}

const LIBRARY_RAG_STAGES = new Set<LibraryRagOrchestrationStage>([
  'request',
  'retrieval',
  'knowledge-planner',
  'selection',
  'compiler',
  'semantic-critic',
])

const LIBRARY_RAG_GAP_CODES = new Set([
  'library-rag-prompt-empty',
  'library-rag-retrieval-failed',
  'library-rag-candidates-missing',
  'library-rag-agent-role-invalid',
  'library-rag-planner-failed',
  'library-rag-selection-invalid',
  'library-rag-critic-failed',
  'library-rag-semantic-rejected',
  'library-input-schema-invalid',
  'library-source-forbidden',
  'library-lineage-missing',
  'ux-pattern-not-admitted',
  'application-blueprint-not-admitted',
  'duplicate-ux-pattern-ref',
  'duplicate-ux-instance-id',
  'duplicate-library-port-id',
  'unbound-pattern-port',
  'incompatible-pattern-port',
  'cross-scope-local-reference',
  'ux-pattern-state-invalid',
  'library-event-invalid',
  'library-capability-invalid',
  'ux-pattern-transition-invalid',
  'journey-entry-invalid',
  'journey-terminal-missing',
  'journey-step-unreachable',
  'journey-recovery-invalid',
  'exact-namespace-collision',
  'library-presentation-forbidden',
  'plan-schema-invalid',
  'release-schema-invalid',
  'plan-state-invalid',
  'semantic-node-missing',
  'node-visibility-invalid',
  'duplicate-node-id',
  'release-not-consumer-verified',
  'target-mismatch',
  'recipe-missing',
  'recipe-authority-ambiguous',
  'region-missing',
  'region-authority-ambiguous',
  'semantic-role-not-admitted',
  'component-missing',
  'component-authority-ambiguous',
  'token-missing',
  'action-slot-missing',
  'action-slot-authority-ambiguous',
  'action-role-not-admitted',
  'capability-not-admitted',
  'exclusive-action-conflict',
  'action-slot-cardinality',
  'primary-action-cardinality',
  'raw-presentation-forbidden',
  'action-semantic-mismatch',
  'design-ontology-release-ref-missing',
  'design-ontology-release-not-found',
  'design-ontology-release-ref-mismatch',
  'design-ontology-release-resolution-failed',
])

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isClosedLibraryRagResult(
  value: unknown,
): value is LibraryRagOrchestrationResult {
  if (!isRecord(value) || typeof value.ok !== 'boolean') return false
  if (value.ok) {
    return validateStudioLibraryRagSuccessReceipt(value).ok
  }
  return (
    typeof value.stage === 'string' &&
    LIBRARY_RAG_STAGES.has(value.stage as LibraryRagOrchestrationStage) &&
    Array.isArray(value.gaps) &&
    value.gaps.length > 0 &&
    value.gaps.every(
      (gap) =>
        isRecord(gap) &&
        typeof gap.code === 'string' &&
        LIBRARY_RAG_GAP_CODES.has(gap.code),
    )
  )
}

function stableOrchestrationFailure(): StudioLibraryAssistantError {
  return new StudioLibraryAssistantError({
    stage: 'retrieval',
    gapCodes: ['library-rag-retrieval-failed'],
  })
}

export function createStudioLibraryAssistant(dependencies: {
  orchestrate: (
    request: LibraryRagOrchestrationRequest,
  ) => Promise<LibraryRagOrchestrationResult>
  startGeneration?: typeof startLibraryRagApplicationGenerationFromPrompt
}): StudioLibraryAssistant {
  const startGeneration =
    dependencies.startGeneration ?? startLibraryRagApplicationGenerationFromPrompt

  return {
    async generate(
      prompt: string,
      options: StudioLibraryAssistantGenerationOptions = {},
    ): Promise<StartAppGenerationOutcome> {
      const normalizedPrompt = prompt.trim()
      if (!normalizedPrompt) {
        throw new StudioLibraryAssistantError({
          stage: 'request',
          gapCodes: ['library-rag-prompt-empty'],
        })
      }

      let resolution: LibraryRagOrchestrationResult
      try {
        const orchestrated = await dependencies.orchestrate({
          prompt: normalizedPrompt,
          target: 'web',
          locale: getLocale(),
          // Platform authority uses a closed request-instance identifier whose
          // first character must be alphabetic. A bare UUID may begin with a
          // digit and would make otherwise identical requests fail randomly.
          applicationInstanceId: `studio-${crypto.randomUUID()}`,
        })
        // Keep the generation boundary isolated from coordinator-owned data.
        // Platform authority is bound to the full canonical result envelope,
        // so a normal data clone preserves it while any mutation fails closed.
        const trusted =
          orchestrated.ok &&
          classifyStudioLibraryRagRuntimeAuthority(orchestrated) ===
            'untrusted'
            ? admitTrustedLegacyStudioLibraryRagResolution(orchestrated)
            : orchestrated
        const snapshot: unknown = structuredClone(trusted)
        if (!isClosedLibraryRagResult(snapshot)) {
          throw stableOrchestrationFailure()
        }
        resolution = snapshot
      } catch {
        throw stableOrchestrationFailure()
      }
      if (!resolution.ok) {
        throw rejectedLibraryRagError(resolution)
      }

      return startGeneration(normalizedPrompt, {
        ...options,
        libraryRagResolution: resolution,
      })
    },
  }
}
